import React, { FC, useState } from 'react';
import cls from 'classnames';
import {
	DatePickerCalendarProps,
	DayPickerParams,
	PickerParams,
} from './DatePickerCalendar.types';
import { getClassNames, getRootClassListFromProps } from '../../../utilities/classNames';
import ChipAction from '../../chip/chipAction/ChipAction';
import ChipChoice from '../../chip/chipChoice/ChipChoice';
import DateChip from '../primitives/dateChip/DateChip';
import ButtonIcon from '../../button/buttonIcon';
import { CaretLeftBoldIcon, CaretRightBoldIcon } from '@stashinvest/react-assets';
import { dayLabels, monthLabels } from '../../../utilities/date';
import { format } from 'date-fns';

export const classNames = getClassNames('sds-date-picker-calendar', [
	'calendar-field',
	'day-field',
	'top-bar',
	'top-bar-left',
	'top-bar-right',
	'chip-picker',
	'year-picker',
	'size-large',
]);
export const defaultMinDate = new Date(1970, 1, 1);
export const defaultMaxDate = new Date(2999, 1, 1);

const normalizeToMidnight = (date: Date, newDay?: number): Date => {
	const newDate = new Date(date);
	const year = newDate.getFullYear();
	const month = newDate.getMonth();
	const day = newDay ?? newDate.getDate();
	return new Date(year, month, day);
};

const getFirstDayOfMonth = (date: Date): number => {
	return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
};

const getDaysInMonth = (date: Date): number => {
	return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
};

const adjustDateMonth = (date: Date, num: number): Date => {
	return new Date(date.getFullYear(), date.getMonth() + num, 1);
};

//renders empty blocks to offset calendar start day
function renderPassThrough(date: Date) {
	const firstDayOfMonth = getFirstDayOfMonth(date);

	const passThroughs = [...Array(firstDayOfMonth).keys()];
	return (
		<>
			{passThroughs.map((i) => (
				<DateChip key={`passthrough-${i}`} />
			))}
		</>
	);
}

function renderDays({
	visibleDate,
	minDate,
	maxDate,
	onClick,
	selectedDate,
	variant,
}: DayPickerParams) {
	const daysInMonth = getDaysInMonth(visibleDate);
	const dayChips = [...Array(daysInMonth).keys()];

	const normSelectedDate = selectedDate ? normalizeToMidnight(selectedDate) : undefined;
	return (
		<>
			{dayChips.map((day) => {
				const chipDate = new Date(
					visibleDate.getFullYear(),
					visibleDate.getMonth(),
					day + 1
				);

				return (
					<DateChip
						key={`day-${day}`}
						date={chipDate}
						isDisabled={chipDate < minDate || chipDate > maxDate}
						isSelected={
							!!normSelectedDate && normSelectedDate.getTime() === chipDate.getTime()
						}
						onClick={() => onClick(chipDate)}
					/>
				);
			})}
		</>
	);
}

function renderMonthPicker({ visibleDate, minDate, maxDate, onClick }: PickerParams) {
	minDate.setDate(0);

	return (
		<div className={classNames.chipPicker}>
			{monthLabels.map((monthLabel, i) => {
				const newDate = new Date(visibleDate);
				newDate.setMonth(i);
				return (
					<ChipAction
						text={monthLabel}
						variant="secondary"
						key={`month-picker-${monthLabel}`}
						isDisabled={newDate < minDate || newDate > maxDate}
						isSelected={visibleDate.getMonth() === i}
						onClick={() => {
							onClick(newDate);
						}}
					/>
				);
			})}
		</div>
	);
}

function renderYearPicker({ visibleDate, minDate, maxDate, onClick }: PickerParams) {
	const yearArr = Array.from(
		{ length: maxDate.getFullYear() - minDate.getFullYear() + 1 },
		(_, i) => minDate.getFullYear() + i
	);
	return (
		<div className={cls(classNames.chipPicker, classNames.yearPicker)}>
			{yearArr.map((year, i) => {
				const newDate = new Date(visibleDate);
				newDate.setFullYear(year);
				return (
					<ChipAction
						text={year}
						variant="secondary"
						key={`year-picker-${year}`}
						isSelected={visibleDate.getFullYear() === year}
						onClick={() => {
							onClick(newDate);
						}}
					/>
				);
			})}
		</div>
	);
}
const DatePickerCalendar: FC<DatePickerCalendarProps> = (props) => {
	const {
		className,
		visibleDate = new Date(),
		selectedDate,
		minDate = defaultMinDate,
		maxDate = defaultMaxDate,
		variant,
		size = 'small',
		onFilterClicked,
		onNextMonthButtonClicked,
		onPreviousMonthButtonClicked,
		onDayClicked,
	} = props;
	const normVisibleDate = normalizeToMidnight(visibleDate, 1);
	const normMinDate = normalizeToMidnight(minDate);
	const normMaxDate = normalizeToMidnight(maxDate);
	const [activePickerType, setActivePickerType] = useState('none');
	return (
		<div
			className={cls(getRootClassListFromProps(classNames, props), className, {
				[classNames.sizeLarge]: props.size === 'large',
			})}
			data-testid={props['data-testid']}
			aria-label={props['aria-label']}
			// eslint-disable-next-line jsx-a11y/aria-props
			data-min={format(minDate, 'yyyy-MM-dd')}
			data-max={format(maxDate, 'yyyy-MM-dd')} //setting these data attributes for test assertions
		>
			<div className={classNames.topBar}>
				<div className={classNames.topBarLeft}>
					<ChipChoice
						text={monthLabels[normVisibleDate.getMonth()]}
						isActive={activePickerType === 'month'}
						onClick={() => {
							setActivePickerType(activePickerType === 'month' ? 'none' : 'month');
						}}
						label="Select Month"
					/>
					<ChipChoice
						text={normVisibleDate.getFullYear()}
						isActive={activePickerType === 'year'}
						onClick={() => {
							setActivePickerType(activePickerType === 'year' ? 'none' : 'year');
						}}
						label="Select Year"
					/>
				</div>

				<div className={classNames.topBarRight}>
					<ButtonIcon
						label="Previous month"
						Icon={CaretLeftBoldIcon}
						size="small"
						variant="secondary"
						onClick={() => {
							const newDate = adjustDateMonth(normVisibleDate, -1);
							onPreviousMonthButtonClicked(newDate);
						}}
					/>
					<ButtonIcon
						label="Next month"
						Icon={CaretRightBoldIcon}
						size="small"
						variant="secondary"
						onClick={() => {
							const newDate = adjustDateMonth(normVisibleDate, 1);
							onNextMonthButtonClicked(newDate);
						}}
					/>
				</div>
			</div>
			<div className={classNames.dayField}>
				{dayLabels.map((day) => {
					return <div key={day}>{day}</div>;
				})}
			</div>

			{activePickerType === 'month' &&
				renderMonthPicker({
					visibleDate: normVisibleDate,
					minDate: normMinDate,
					maxDate: normMaxDate,
					onClick: (newDate) => {
						onFilterClicked(newDate);
						setActivePickerType('none');
					},
				})}
			{activePickerType === 'year' &&
				renderYearPicker({
					visibleDate: normVisibleDate,
					minDate: normMinDate,
					maxDate: normMaxDate,
					onClick: (newDate) => {
						setActivePickerType('none');
						onFilterClicked(newDate);
					},
				})}
			{activePickerType === 'none' && (
				<div className={classNames.calendarField}>
					{renderPassThrough(normVisibleDate)}
					{renderDays({
						visibleDate: normVisibleDate,
						minDate: normMinDate,
						maxDate: normMaxDate,
						onClick: onDayClicked,
						selectedDate,
						variant,
					})}
				</div>
			)}
		</div>
	);
};

DatePickerCalendar.displayName = 'DatePickerCalendar';

export default DatePickerCalendar;
